import { useHighscores } from '@/hooks/useHighscores';
import { formatNumber } from '@/lib/utils';

interface TopScoresProps {
  gameId: string;
  gameName: string;
}

export function TopScores({ gameId, gameName }: TopScoresProps) {
  const { data: scores, isLoading, error } = useHighscores(gameId, 5);

  return (
    <div className="card p-4 sm:p-6">
      <h3 className="font-semibold text-text-primary mb-3 sm:mb-4 text-sm sm:text-base">{gameName}</h3>
      
      {isLoading ? (
        <div className="space-y-2 sm:space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex justify-between animate-pulse">
              <div className="h-4 sm:h-5 bg-border-dark rounded w-24 sm:w-32" />
              <div className="h-4 sm:h-5 bg-border-dark rounded w-12 sm:w-16" />
            </div>
          ))}
        </div>
      ) : error ? (
        <p className="text-text-muted text-xs sm:text-sm">Unable to load scores</p>
      ) : scores && scores.length > 0 ? (
        <div className="space-y-2 sm:space-y-3">
          {scores.map((score, index) => (
            <div 
              key={score.id} 
              className="flex items-center justify-between text-xs sm:text-sm"
            >
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <span className={getRankClass(index)}>
                  {index + 1}.
                </span>
                <span className="text-text-primary truncate">
                  {score.player_name}
                </span>
              </div>
              <span className="font-semibold text-text-primary shrink-0 ml-2">
                {formatNumber(score.score)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-text-muted text-xs sm:text-sm">No scores yet. Be the first!</p>
      )}
    </div>
  );
}

function getRankClass(index: number): string {
  const classes = [
    'text-accent-gold font-bold',   // 1st
    'text-gray-400 font-bold',      // 2nd
    'text-orange-400 font-bold',    // 3rd
    'text-text-muted',              // 4th+
    'text-text-muted',              // 5th
  ];
  return classes[index] || 'text-text-muted';
}
